import { Delete, Edit } from '@mui/icons-material';
import { Alert, CardContent } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { updateUserProject } from '../../../modules/project';
import Modal from '../../../ui/layout/Modal';
import UserProjectForm from './UserProjectForm';
import DeleteProject from './DeleteProject';
import CustomIconButton from '../../elements/CustomIconButton';
import { useNotification } from '../../../hooks/NotificationContext';
import { useInvalidateQuery } from '../../../query/apiQueries';

const EditUserProject = ({
  selectedProject,
  fetchProjectData,
  controlled = false,
  open: externalOpen = false,
  onClose: externalOnClose,
  deleteOpen: externalDeleteOpen = false,
  onDeleteClose: externalOnDeleteClose,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [internalDeleteOpen, setInternalDeleteOpen] = useState(false);
  const [error, setError] = useState(null);

  const currentUserId = JSON.parse(localStorage.getItem('user')).id;
  const { addNotification } = useNotification();
  const invalidateQueries = useInvalidateQuery();

  const initialValues = {
    name: selectedProject.name,
    description: selectedProject.description,
    reviewWorkflow: selectedProject.review_workflow,
    mandatoryAssignedTo: selectedProject.mandatory_assigned_to,
    defaultAssignedTo: selectedProject.default_assigned_to || currentUserId,
  };

  // Resolve open state from either controlled or internal source
  const open = controlled ? externalOpen : internalOpen;
  const deleteConfirmationOpen = controlled ? externalDeleteOpen : internalDeleteOpen;

  const handleClickOpen = useCallback(() => {
    setError(null);
    if (!controlled) setInternalOpen(true);
  }, [controlled]);

  const handleClose = useCallback(() => {
    if (controlled) externalOnClose?.();
    else setInternalOpen(false);
  }, [controlled, externalOnClose]);

  const handleDeleteButtonClick = () => {
    if (!controlled) setInternalDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    if (controlled) externalOnDeleteClose?.();
    else setInternalDeleteOpen(false);
  };

  const handleSave = async (values) => {
    const project = {
      name: values.name,
      description: values.description,
      id: selectedProject.id,
      reviewWorkflow: values.reviewWorkflow,
      mandatoryAssignedTo: values.mandatoryAssignedTo,
      defaultAssignedTo: values.defaultAssignedTo,
    };
    try {
      await updateUserProject(project);
      addNotification('success', 'Project updated successfully!');
      fetchProjectData();
      handleClose();
      invalidateQueries([
        { key: 'project', type: 'headerProjects' },
        { key: 'project', type: 'dashboardProjects' },
        { key: 'project', type: 'list' },
      ]);
    } catch (err) {
      setError(err.message || 'Failed to update project. Please try again.');
    }
  };

  return (
    <>
      {/* Own trigger buttons — only rendered in standalone (non-controlled) mode */}
      {!controlled && (
        <>
          <CustomIconButton
            title="Edit Project"
            disableRipple
            onClick={handleClickOpen}
          >
            <Edit />
          </CustomIconButton>
          <CustomIconButton
            title="Delete Project"
            disableRipple
            onClick={handleDeleteButtonClick}
          >
            <Delete sx={{ color: 'red' }} />
          </CustomIconButton>
        </>
      )}

      {/* Edit modal */}
      <Modal
        open={open}
        width="md"
        modalTitle="Edit Project"
        handleClose={handleClose}
      >
        <CardContent sx={{ pt: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <UserProjectForm
            initialValues={initialValues}
            onSubmit={handleSave}
            selectedProject={selectedProject}
            error={error}
            type="edit"
          />
        </CardContent>
      </Modal>

      {/* Delete confirmation modal */}
      <DeleteProject
        open={deleteConfirmationOpen}
        handleDeleteCancel={handleDeleteCancel}
        selectedProject={selectedProject}
        setDeleteConfirmationOpen={
          controlled ? externalOnDeleteClose : setInternalDeleteOpen
        }
        fetchProjectData={fetchProjectData}
      />
    </>
  );
};

export default EditUserProject;